import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Label } from '@/components/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { toast } from 'sonner'
import type { RedirectPreview } from './redirectTypes'

interface RedirectImportProps {
  content: string
  format: string
  mode: string
  busy: boolean
  error?: string
  preview: RedirectPreview | null
  previewCurrent: boolean
  onContent: (value: string) => void
  onFormat: (value: string) => void
  onMode: (value: string) => void
  onPreview: () => void
  onApply: () => void
}

export function RedirectImport({ content, format, mode, busy, error, preview, previewCurrent, onContent, onFormat, onMode, onPreview, onApply }: RedirectImportProps) {
  const oversized = new globalThis.TextEncoder().encode(content).byteLength > 5 * 1024 * 1024
  return (
    <section className="space-y-4 rounded-lg border border-border p-5">
      <div><h3 className="font-semibold">Import redirect map</h3>
        <p className="text-sm text-muted-foreground">Use paths on this help center: old path, current path, then 301 or 302. Omitted status defaults to 301.</p>
        <p className="text-xs text-muted-foreground mt-1">Exact paths only; trailing slashes are equivalent. Comments and blank lines are supported. New or changed destinations must be published pages. Wildcards and external URLs are unsupported.</p></div>
      <div className="flex flex-wrap gap-3">
        <Select value={format} onValueChange={onFormat}><SelectTrigger className="w-48" aria-label="Import format"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="text">Plain text</SelectItem><SelectItem value="csv">CSV</SelectItem></SelectContent></Select>
        <Select value={mode} onValueChange={onMode}><SelectTrigger className="w-56" aria-label="Import mode"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="merge">Merge new rules</SelectItem><SelectItem value="replace">Replace entire map</SelectItem></SelectContent></Select>
        <Button variant="outline" asChild><label>Choose file
          <input type="file" accept=".csv,.txt,_redirects" className="sr-only" onChange={async e => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file && file.size > 5 * 1024 * 1024) { toast.error('Redirect maps must be at most 5 MB.'); return }
            if (file) { onFormat(file.name.endsWith('.csv') ? 'csv' : 'text'); onContent(await file.text()) }
          }} /></label></Button>
      </div>
      <Label htmlFor="redirect-import">{format === 'csv' ? 'CSV headers: source,destination,status' : 'One rule per line: /old-path /articles/current-path 301'}</Label>
      <textarea id="redirect-import" value={content} onChange={e => onContent(e.target.value)} rows={8}
        className="w-full rounded-md border border-input bg-transparent p-3 font-mono text-sm" spellCheck={false} />
      {mode === 'replace' && <p className="text-sm text-muted-foreground">Replacement removes saved rules absent from this map. An empty replacement clears all redirects.</p>}
      {oversized && <p role="alert" className="text-sm text-destructive">Redirect maps must be at most 5 MB.</p>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <Button disabled={busy || oversized} onClick={onPreview}>Validate and preview</Button>
      {preview && previewCurrent && (
        <div className="space-y-3">
          <p className="text-sm">{preview.added} added · {preview.changed} changed · {preview.removed} removed · {preview.rules.length} total rules</p>
          {preview.errors.length > 0 ? <ul role="alert" className="list-disc pl-5 text-sm text-destructive">{preview.errors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}</ul> : <>
            <div className="max-h-64 overflow-auto rounded-md bg-muted/30 p-3 font-mono text-xs whitespace-pre">{preview.rules.map(r => `${r.source} ${r.destination} ${r.status}`).join('\n')}</div>
            <ConfirmDialog title="Apply redirect map?" description={`Save ${preview.rules.length} redirects, including ${preview.removed} removals. Changes take effect immediately; redirect responses are served without browser caching.`}
              trigger={<Button disabled={busy}>Apply redirects</Button>} confirmText="Apply redirects" onConfirm={onApply} />
          </>}
        </div>
      )}
    </section>
  )
}
